import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { Truck } from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { showToast } from '@/components/ui/Toast';
import { PullToRefresh } from '@/components/PullToRefresh';
import { assignmentRepo } from '@/data/repositories/assignmentRepo';
import { syncService } from '@/services/sync';
import { gpsService } from '@/services/gps';
import { logQueueService } from '@/services/logQueue';
import { haptics } from '@/lib/haptics';
import { groupStops, aggregateBills, dispatchLabel, type Stop } from '@/lib/grouping';
import { LogTypes } from '@/types/domain';

export function UnloadPage() {
  const navigate = useNavigate();
  const [dock, setDock] = useState(false);
  const [backToBaseLogged, setBackToBaseLogged] = useState(false);
  const [arrivedLogged, setArrivedLogged] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    syncService.setContext('loadUnload');
  }, []);

  const items = useLiveQuery(() => assignmentRepo.getUnloadItems(), [], []);
  const stops = items ? groupStops(items, 'delivery', dock) : undefined;
  const dockBills = dock && stops ? stops.flatMap((s) => s.bills) : [];

  async function onBackToBase() {
    setBusy(true);
    try {
      const fix = gpsService.peekFix();
      await logQueueService.enqueue({
        logType: LogTypes.backToBase,
        display: 'Display',
        latitude: fix?.latitude,
        longitude: fix?.longitude,
        accuracy: fix?.accuracy,
      });
      haptics.light();
      setBackToBaseLogged(true);
    } finally {
      setBusy(false);
    }
  }

  async function onArrivedAtTerminal() {
    setBusy(true);
    try {
      const fix = gpsService.peekFix();
      await logQueueService.enqueue({
        logType: LogTypes.arrivedAtTerminal,
        display: 'Display',
        latitude: fix?.latitude,
        longitude: fix?.longitude,
        accuracy: fix?.accuracy,
      });
      haptics.light();
      setArrivedLogged(true);
    } finally {
      setBusy(false);
    }
  }

  async function onUnloadAtDock() {
    if (dockBills.length === 0) return;
    setBusy(true);
    try {
      const fix = gpsService.peekFix();
      for (const b of dockBills) {
        await logQueueService.enqueue({
          logType: LogTypes.unloadedAtTerminalDock,
          display: 'Display',
          assignmentId: b.remF04Id,
          tag: b.remF02Tag ?? undefined,
          latitude: fix?.latitude,
          longitude: fix?.longitude,
          accuracy: fix?.accuracy,
        });
        await assignmentRepo.markComplete(b.remF04Id);
      }
      haptics.medium();
      showToast(`${dockBills.length} item(s) unloaded at dock.`, 'success');
      navigate('/main');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell title="Deliveries" showBack>
      <PullToRefresh onRefresh={() => syncService.runOnce('manual')}>
      <div className="mb-3 flex gap-2">
        <Button variant={!dock ? 'delivery' : 'ghost'} size="sm" onClick={() => setDock(false)}>
          DELIVERY
        </Button>
        <Button variant={dock ? 'delivery' : 'ghost'} size="sm" onClick={() => setDock(true)}>
          DOCK DELIVERY
        </Button>
      </div>

      {stops === undefined ? (
        <p className="text-brand-muted">Loading...</p>
      ) : stops.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-brand-muted">No deliveries in this list.</p>
            <Button variant="primary" onClick={() => void syncService.runOnce('manual')}>
              Refresh
            </Button>
          </CardBody>
        </Card>
      ) : (
        <ul className="space-y-3">
          {stops.map((s) => (
            <StopCard
              key={s.key}
              stop={s}
              dock={dock}
              onOpen={() => navigate(`/unload/${s.bills[0].remF04Id}`)}
            />
          ))}
        </ul>
      )}

      {dock && dockBills.length > 0 && (
        <div className="pb-2-safe sticky bottom-0 mt-4 space-y-2 bg-brand-bg pt-3">
          {!backToBaseLogged ? (
            <Button variant="outline" size="lg" fullWidth disabled={busy} onClick={() => void onBackToBase()}>
              BACK TO BASE
            </Button>
          ) : !arrivedLogged ? (
            <>
              <div className="flex items-center justify-center gap-2 rounded-md border border-brand-error bg-brand-error/15 py-1.5 text-xs font-bold text-brand-error">
                <Truck className="h-4 w-4" />
                RETURNING TO TERMINAL
              </div>
              <Button variant="danger" size="xl" fullWidth disabled={busy} onClick={() => void onArrivedAtTerminal()}>
                ARRIVED AT TERMINAL
              </Button>
            </>
          ) : (
            <Button variant="delivery" size="xl" fullWidth disabled={busy} onClick={() => void onUnloadAtDock()}>
              UNLOAD AT DOCK
            </Button>
          )}
        </div>
      )}
      </PullToRefresh>
    </AppShell>
  );
}

function StopCard({ stop, dock, onOpen }: { stop: Stop; dock: boolean; onOpen: () => void }) {
  const { pcs, wgt, refs } = aggregateBills(stop.bills);
  const label = dispatchLabel(stop.bills[0]);
  return (
    <li>
      <Card
        accent="delivery"
        className={dock ? undefined : 'cursor-pointer'}
        onClick={dock ? undefined : onOpen}
      >
        <CardHeader>
          <CardTitle>{stop.name || 'Delivery'}</CardTitle>
          <span className="rounded bg-brand-delivery/20 px-1.5 py-0.5 text-xs font-bold text-brand-delivery">
            {label}
          </span>
        </CardHeader>
        <CardBody>
          <div className="text-sm">
            <strong>To:</strong> {stop.address || '--'}
            {stop.city ? `, ${stop.city}` : ''}
          </div>
          <div className="mt-1 text-xs text-brand-muted">
            {stop.bills.length} bill{stop.bills.length === 1 ? '' : 's'} &middot; {pcs} pcs &middot;{' '}
            {wgt} lbs
          </div>
          {refs && <div className="mt-1 truncate text-xs text-brand-muted">Ref: {refs}</div>}
          <div className="mt-1 text-xs text-brand-muted">
            {stop.bills.map((b) => b.remF02Tag).filter(Boolean).join(', ')}
          </div>
        </CardBody>
      </Card>
    </li>
  );
}
